import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'
import { getDailyBreakdown } from '@/api/attendance'
import { cn } from '@/lib/utils'

export const LEAVE_TYPE_LABELS: Record<string, string> = {
  casual: 'Casual', sick: 'Sick', urgent: 'Urgent', annual: 'Annual', wfh: 'WFH',
}

/** Per-day attendance for a user. Defaults to the last 30 days when no
 * `start`/`end` is given (unchanged behavior for existing callers). */
export default function AttendanceTimeline({ userId, start, end }: { userId: string; start?: string; end?: string }) {
  const q = useQuery({
    queryKey: ['attendance-daily', userId, start, end],
    queryFn: () => getDailyBreakdown(userId, start, end),
    enabled: !!userId,
  })

  if (q.isLoading) return <div className="py-8 text-center text-sm text-gray-400">Loading attendance…</div>
  if (q.isError || !q.data) return <div className="py-8 text-center text-sm text-red-500">Couldn’t load attendance.</div>

  const { days } = q.data
  // Most recent first.
  const rows = [...days].reverse()

  const stats = [
    { label: 'Present', value: q.data.present_days, tone: 'text-green-600' },
    { label: 'Absent', value: q.data.absent_days, tone: 'text-red-600' },
    { label: 'Leave', value: q.data.leave_days, tone: 'text-amber-600' },
    { label: 'WFH', value: q.data.wfh_days, tone: 'text-cyan-600' },
    { label: 'Late', value: q.data.late_days, tone: 'text-orange-600' },
    { label: 'Hours', value: q.data.worked_hours, tone: 'text-gray-800' },
  ]

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-3 gap-2 sm:grid-cols-6">
        {stats.map((s) => (
          <div key={s.label} className="rounded-lg bg-gray-50 px-2 py-2 text-center">
            <div className={cn('text-lg font-bold', s.tone)}>{s.value}</div>
            <div className="text-[10px] uppercase tracking-wide text-gray-400">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Daily list — table on sm+, stacked cards on mobile */}
      <div className="hidden overflow-hidden rounded-lg border border-gray-100 sm:block">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50 text-left text-xs text-gray-500">
              <th className="px-3 py-2 font-semibold">Date</th>
              <th className="px-3 py-2 font-semibold">In</th>
              <th className="px-3 py-2 font-semibold">Out</th>
              <th className="px-3 py-2 text-right font-semibold">Duration</th>
              <th className="px-3 py-2 text-right font-semibold">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.date} className={cn('border-b border-gray-50 last:border-0', rowTint(d.status))}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{format(new Date(d.date + 'T00:00:00'), 'EEE, dd MMM')}</div>
                </td>
                <td className="px-3 py-2 text-gray-600">{fmtTime(d.checkin_time)}</td>
                <td className="px-3 py-2 text-gray-600">
                  {d.is_active ? <span className="text-blue-600">Active</span> : fmtTime(d.checkout_time)}
                </td>
                <td className="px-3 py-2 text-right text-gray-700">{d.worked_hours ? fmtDur(d.worked_minutes) : '—'}</td>
                <td className="px-3 py-2 text-right"><StatusBadge status={d.status} late={d.is_late} lateMins={d.late_minutes} leaveType={d.leave_type} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile cards */}
      <div className="space-y-2 sm:hidden">
        {rows.map((d) => (
          <div key={d.date} className={cn('flex items-center justify-between rounded-lg border border-gray-100 p-3', rowTint(d.status))}>
            <div>
              <div className="font-medium text-gray-800">{format(new Date(d.date + 'T00:00:00'), 'EEE, dd MMM')}</div>
              <div className="text-xs text-gray-500">
                {fmtTime(d.checkin_time)} – {d.is_active ? 'Active' : fmtTime(d.checkout_time)}
                {d.worked_hours ? ` · ${fmtDur(d.worked_minutes)}` : ''}
              </div>
            </div>
            <StatusBadge status={d.status} late={d.is_late} lateMins={d.late_minutes} leaveType={d.leave_type} />
          </div>
        ))}
      </div>
    </div>
  )
}

export function fmtTime(iso: string | null): string {
  if (!iso) return '—'
  return format(new Date(iso), 'h:mm a')
}

export function fmtDur(mins: number): string {
  const h = Math.floor(mins / 60)
  const m = mins % 60
  return `${h}h ${m}m`
}

function rowTint(status: string): string {
  if (status === 'absent') return 'bg-red-50/40'
  if (status === 'holiday' || status === 'off') return 'bg-gray-50/60'
  return ''
}

export const STATUS_STYLE: Record<string, { label: string; cls: string }> = {
  completed: { label: 'Present', cls: 'bg-green-100 text-green-700' },
  overtime: { label: 'Overtime', cls: 'bg-violet-100 text-violet-700' },
  less_hours: { label: 'Short Hours', cls: 'bg-amber-100 text-amber-700' },
  working: { label: 'Working', cls: 'bg-blue-100 text-blue-700' },
  wfh: { label: 'WFH', cls: 'bg-cyan-100 text-cyan-700' },
  leave: { label: 'Leave', cls: 'bg-yellow-100 text-yellow-700' },
  absent: { label: 'Absent', cls: 'bg-red-100 text-red-700' },
  holiday: { label: 'Holiday', cls: 'bg-slate-100 text-slate-600' },
  off: { label: 'Off', cls: 'bg-gray-100 text-gray-500' },
}

export function StatusBadge({ status, late, lateMins, leaveType }: { status: string; late: boolean; lateMins: number; leaveType?: string | null }) {
  const s = STATUS_STYLE[status] ?? { label: status, cls: 'bg-gray-100 text-gray-600' }
  // For a "leave" day, show which specific type (casual/sick/annual/…) rather
  // than just the generic "Leave" badge.
  const label = status === 'leave' && leaveType ? (LEAVE_TYPE_LABELS[leaveType] || leaveType) : s.label
  return (
    <span className="inline-flex items-center gap-1 whitespace-nowrap">
      <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', s.cls)}>{label}</span>
      {late && (status === 'completed' || status === 'overtime' || status === 'less_hours' || status === 'working') && (
        <span className="shrink-0 rounded-full bg-orange-100 px-1.5 py-0.5 text-[10px] font-medium text-orange-700">+{lateMins}m late</span>
      )}
    </span>
  )
}
